/**
 * The bar is `fixed` against the bottom-right corner with a drag `transform` on top, so
 * positions here are offsets from that anchor: x <= 0 moves left, y <= 0 moves up, {0,0} is home.
 */

/** Must match the `bottom-*` utility on the bar before any safe-area inset. */
export const DOCK_ANCHOR_BOTTOM_PX = 48;
/** Must match the `right-*` utility on the bar. */
export const DOCK_ANCHOR_RIGHT_PX = 16;
export const DOCK_EDGE_MARGIN_PX = 8;
export const DRAG_THRESHOLD_PX = 4;
// Tap, scroll and drag all start as a finger on the icon, so only time separates them.
export const DOCK_HOLD_MS = 400;
// A finger rolls on any press, so the hold survives some movement before it reads as a scroll.
export const DOCK_HOLD_TOLERANCE_PX = 8;
export const DOCK_NUDGE_PX = 16;
export const DOCK_NUDGE_FAST_PX = 64;
export const DOCK_DRAG_HINT_ID = 'floating-action-bar-drag-hint';

// The grip is the visible handle, so a press landing on it drags without waiting out the hold.
export const DOCK_GRIP_CLASS = 'fab-drag-grip';
export const DOCK_GRIP_SELECTOR = `.${DOCK_GRIP_CLASS}`;

export const isWithinDockGrip = (target: EventTarget | null): boolean =>
    target instanceof Element && target.closest(DOCK_GRIP_SELECTOR) !== null;

export interface DockOffset {
    x: number;
    y: number;
}

export interface DockBox {
    width: number;
    height: number;
}

/** Distance from the viewport's right and bottom edges to the bar's resting corner. */
export interface DockAnchor {
    right: number;
    bottom: number;
}

export const DEFAULT_DOCK_ANCHOR: DockAnchor = {
    right: DOCK_ANCHOR_RIGHT_PX,
    bottom: DOCK_ANCHOR_BOTTOM_PX,
};

/** The parts of a DOMRect this module needs so callers can test it without a real one. */
export interface DockRect {
    width: number;
    height: number;
    right: number;
    bottom: number;
}

// Measured rather than taken from the constants above, since `env(safe-area-inset-bottom)` is
// unknowable from JS and assuming it away lets a drag push the bar off the top. Null for a
// hidden bar, whose 0x0 rect would read as an anchor the size of the viewport.
export const measureDockAnchor = (
    rect: DockRect,
    viewport: DockBox,
    offset: DockOffset,
): DockAnchor | null => {
    if (rect.width <= 0 || rect.height <= 0) {
        return null;
    }
    // The rect includes the drag transform so subtract the offset back out to get the resting inset.
    const right = viewport.width - rect.right + offset.x;
    const bottom = viewport.height - rect.bottom + offset.y;
    if (!Number.isFinite(right) || !Number.isFinite(bottom)) {
        return null;
    }
    return { right: Math.max(0, right), bottom: Math.max(0, bottom) };
};

/** Stored as fractions of available travel (0 = at the anchor, 1 = against the opposite edge). */
export interface StoredDockPosition {
    xf: number;
    yf: number;
}

export const DOCK_POSITION_STORAGE_PREFIX = 'fab-position-v1';

export const dockPositionStorageKey = (userId: string): string =>
    `${DOCK_POSITION_STORAGE_PREFIX}-${userId}`;

// Floors at zero. On a viewport smaller than the bar, travel goes negative and the inverted
// bounds clamp to a nonsense position.
export const dockTravel = (
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): DockBox => ({
    width: Math.max(0, viewport.width - size.width - anchor.right - DOCK_EDGE_MARGIN_PX),
    height: Math.max(0, viewport.height - size.height - anchor.bottom - DOCK_EDGE_MARGIN_PX),
});

export interface DockBounds {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

export const dockBounds = (
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): DockBounds => {
    const travel = dockTravel(viewport, size, anchor);
    return { left: -travel.width, top: -travel.height, right: 0, bottom: 0 };
};

export const sameDockBounds = (a: DockBounds, b: DockBounds): boolean =>
    a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;

/** Returns the identical object when already in range so callers can skip a re-render. */
export const clampDockOffset = (
    offset: DockOffset,
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): DockOffset => {
    const bounds = dockBounds(viewport, size, anchor);
    const x = Math.min(bounds.right, Math.max(bounds.left, offset.x));
    const y = Math.min(bounds.bottom, Math.max(bounds.top, offset.y));
    return x === offset.x && y === offset.y ? offset : { x, y };
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export const offsetToFraction = (
    offset: DockOffset,
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): StoredDockPosition => {
    const travel = dockTravel(viewport, size, anchor);
    return {
        xf: travel.width > 0 ? clamp01(-offset.x / travel.width) : 0,
        yf: travel.height > 0 ? clamp01(-offset.y / travel.height) : 0,
    };
};

// Fractions survive a screen change where pixels do not, and are always in bounds, so restoring
// needs no clamp. A bar parked mid-screen on a monitor comes back mid-screen on a laptop.
export const fractionToOffset = (
    stored: StoredDockPosition,
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): DockOffset => {
    const travel = dockTravel(viewport, size, anchor);
    return {
        x: -clamp01(stored.xf) * travel.width,
        y: -clamp01(stored.yf) * travel.height,
    };
};

const isFraction = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

// Validated rather than cast. A truncated value would put NaN into a CSS transform, breaking
// positioning silently.
export const readStoredDockPosition = (userId: string): StoredDockPosition | null => {
    try {
        const raw = localStorage.getItem(dockPositionStorageKey(userId));
        if (!raw) {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }
        const { xf, yf } = parsed as Record<string, unknown>;
        return isFraction(xf) && isFraction(yf) ? { xf, yf } : null;
    }
    catch {
        return null;
    }
};

export const writeStoredDockPosition = (
    userId: string,
    offset: DockOffset,
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): void => {
    try {
        localStorage.setItem(
            dockPositionStorageKey(userId),
            JSON.stringify(offsetToFraction(offset, viewport, size, anchor)),
        );
    }
    catch {
        // Quota exceeded or storage blocked so the position will not persist.
    }
};

export const describeDockPosition = (
    offset: DockOffset,
    viewport: DockBox,
    size: DockBox,
    anchor: DockAnchor = DEFAULT_DOCK_ANCHOR,
): string => {
    const { xf, yf } = offsetToFraction(offset, viewport, size, anchor);
    const vertical = yf > 0.66 ? 'top' : yf > 0.33 ? 'middle' : 'bottom';
    const horizontal = xf > 0.66 ? 'left' : xf > 0.33 ? 'center' : 'right';
    return `${vertical} ${horizontal}`;
};
